"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeft, ArrowLeftRight, ArrowRight, BadgeCheck, CircleSlash, HandCoins, Sparkles, Tag } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { CollectionActivityItem, CollectionListingEvent, CollectionOfferEvent } from "@/features/collections/types";
import { ACTIVITY_MAX_PAGES, ACTIVITY_PAGE_SIZE } from "@/features/collections/activity-pagination";
import { activityKey, EVENT_WORDS, eventKind, eventPrice, eventTokenId, eventTone, eventUser, isTransferEvent, type EventKind } from "@/features/collections/activity-events";
import { SecGlyph } from "@/features/home-v2/ornament";
import { avatarOf } from "@/features/home-v2/tiles";
import { Avatar, Spinner, StatePanel } from "@/features/home-v2/ui";
import { sanitizeUrl } from "@/lib/helpers";
import { useActivityFeed } from "../data/useDetailData";
import FitHex from "../FitHex";
import { Note, RetryButton, RowsSkeleton, tokenAmount, tokenDigits, TxLink } from "../parts";
type Props = {
  contract: string;
  lang: "fa" | "en";
  t: Dictionary["collectionPage"];
  states: Dictionary["states"];
  emptyAction: ReactNode;
};
function dayKey(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** "Today", "Yesterday", or the date in words — how a feed is read. */
function dayLabel(iso: string, locale: string, isFa: boolean) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return isFa ? "امروز" : "Today";
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return isFa ? "دیروز" : "Yesterday";
  return d.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long"
  });
}
function Who(t0) {
  const $ = _c(7);
  const {
    name,
    address
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = name ? <b>{name}</b> : null;
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== address) {
    t2 = address ? <FitHex value={address} /> : <span>—</span>;
    $[2] = address;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <span className="vt-who">{t1}{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
const MARK: Record<EventKind, ReactNode> = {
  mint: <Sparkles />,
  transfer: <ArrowLeftRight />,
  offer: <HandCoins />,
  "offer-accepted": <BadgeCheck />,
  "offer-sold": <BadgeCheck />,
  "offer-cancelled": <CircleSlash />,
  "offer-failed": <CircleSlash />,
  listing: <Tag />,
  "listing-cancelled": <CircleSlash />,
  "listing-failed": <CircleSlash />
};

/** Another market's name on its record; NFTBaz's own (and a bare on-chain one) need none. */
function marketName(m: string | undefined) {
  const v = m?.trim().toLowerCase();
  if (!v || v === "nftbaz" || v === "onchain") return null;
  return v === "opensea" ? "OpenSea" : m!.trim();
}

/** The work a market row is about — its picture and name, sent with the row. */
function EventWork(t0) {
  const $ = _c(22);
  const {
    e,
    contract,
    lang
  } = t0;
  const isFa = lang === "fa";
  let image;
  let t1;
  let tokenId;
  if ($[0] !== e || $[1] !== isFa) {
    tokenId = eventTokenId(e);
    let t2;
    if ($[5] !== e.nft?.name) {
      t2 = e.nft?.name?.trim() || null;
      $[5] = e.nft?.name;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    const name = t2;
    let t3;
    if ($[7] !== e.nft?.image) {
      t3 = sanitizeUrl(e.nft?.image ?? undefined) || null;
      $[7] = e.nft?.image;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    image = t3;
    t1 = name ?? (tokenId ? `#${tokenDigits(tokenId, isFa)}` : "\u2014");
    $[0] = e;
    $[1] = isFa;
    $[2] = image;
    $[3] = t1;
    $[4] = tokenId;
  } else {
    image = $[2];
    t1 = $[3];
    tokenId = $[4];
  }
  const label = t1;
  if (!tokenId) {
    let t2;
    if ($[9] !== label) {
      t2 = <span className="vt-ev-token">{label}</span>;
      $[9] = label;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  const href = `/${lang}/assets/${e.chain || "ethereum"}/${e.nft?.contract_address || e.contract_address || contract}/${tokenId}`;
  const t2 = `#${tokenId}`;
  let t3;
  if ($[11] !== image) {
    t3 = image ? <Image src={image} alt="" fill={true} sizes="28px" /> : null;
    $[11] = image;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t3) {
    t4 = <span className="vt-bid-thumb">{t3}</span>;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== label) {
    t5 = <span>{label}</span>;
    $[15] = label;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== href || $[18] !== t2 || $[19] !== t4 || $[20] !== t5) {
    t6 = <Link className="vt-bid-nft vt-ev-work v2-focus" href={href} title={t2}>{t4}{t5}</Link>;
    $[17] = href;
    $[18] = t2;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}

/** Who made the record: the NFTBaz account (avatar, name, link), or a wallet from another market. */
function Account(t0) {
  const $ = _c(19);
  const {
    e,
    lang
  } = t0;
  let t1;
  if ($[0] !== e.user) {
    t1 = eventUser(e.user);
    $[0] = e.user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const u = t1;
  const address = (e.type === "offer" ? e.offerer : e.seller) ?? undefined;
  if (!u) {
    let t2;
    if ($[2] !== address) {
      t2 = address ? <span className="vt-who"><FitHex value={address} /></span> : <span className="vt-who">—</span>;
      $[2] = address;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const t2 = u.name;
  let t3;
  if ($[4] !== e.user) {
    t3 = avatarOf(e.user);
    $[4] = e.user;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3 || $[7] !== u.name) {
    t4 = <Avatar name={t2} src={t3} size={22} />;
    $[6] = t3;
    $[7] = u.name;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== u.name) {
    t5 = <span>{u.name}</span>;
    $[9] = u.name;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const body = t6;
  let t7;
  if ($[14] !== address || $[15] !== body || $[16] !== lang || $[17] !== u.username) {
    t7 = u.username ? <Link href={`/${lang}/artists/${encodeURIComponent(u.username)}`} className="vt-bid-who v2-focus" title={address}>{body}</Link> : <span className="vt-bid-who" title={address}>{body}</span>;
    $[14] = address;
    $[15] = body;
    $[16] = lang;
    $[17] = u.username;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}

/**
 * THE COLLECTION'S ACTIVITY AS A TIMELINE — `useActivityFeed`, the legacy paging unchanged,
 * grouped by day under "today / yesterday / the date". Each row is what the backend says it
 * is (`activity-events`): a mint or a transfer (who → whom, the transaction), an offer
 * (made, accepted, sold, cancelled) or a listing (listed, cancelled) — the work, the account,
 * the amount in its own currency. Each kind is a mark on the rail and its word.
 */
export default function VitrineActivity(t0) {
  const $ = _c(20);
  const {
    contract,
    lang,
    t,
    states,
    emptyAction
  } = t0;
  const isFa = lang === "fa";
  const locale = isFa ? "fa-IR" : "en-US";
  const nf = new Intl.NumberFormat(locale);
  const {
    query,
    rows,
    stop,
    retry
  } = useActivityFeed(contract, lang);
  const Toward = isFa ? ArrowLeft : ArrowRight;
  if (query.isLoading && rows.length === 0) {
    let t1;
    if ($[0] !== states.loading) {
      t1 = <RowsSkeleton label={states.loading} />;
      $[0] = states.loading;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (rows.length === 0) {
    let t1;
    if ($[2] !== emptyAction || $[3] !== query.isSuccess || $[4] !== retry || $[5] !== states || $[6] !== t) {
      t1 = query.isSuccess ? <StatePanel title={t.activityEmptyTitle} body={t.activityEmptyBody} action={emptyAction} /> : <StatePanel alert={true} title={states.errorTitle} body={states.errorBody} action={<RetryButton label={states.retry} onRetry={retry} />} />;
      $[2] = emptyAction;
      $[3] = query.isSuccess;
      $[4] = retry;
      $[5] = states;
      $[6] = t;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  const days = [];
  for (const a of rows) {
    const key = dayKey(a.timestamp);
    const last = days[days.length - 1];
    if (last?.key === key) {
      last.items.push(a);
    } else {
      days.push({
        key,
        label: dayLabel(a.timestamp, locale, isFa),
        items: [a]
      });
    }
  }
  let t1;
  if ($[8] !== t.activityTableLabel) {
    t1 = <p className="v2-sr-only">{t.activityTableLabel}</p>;
    $[8] = t.activityTableLabel;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== query || $[11] !== t.loadMoreActivity) {
    t2 = query.hasNextPage || query.isFetchingNextPage ? <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage} aria-busy={query.isFetchingNextPage || undefined}>{query.isFetchingNextPage ? <Spinner /> : null}{t.loadMoreActivity}</button> : null;
    $[10] = query;
    $[11] = t.loadMoreActivity;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== stop || $[14] !== t.activityPaginationStopped) {
    t3 = stop === "repeated-page" || stop === "no-new-events" ? <Note>{t.activityPaginationStopped}</Note> : null;
    $[13] = stop;
    $[14] = t.activityPaginationStopped;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== query.isError || $[17] !== retry || $[18] !== states) {
    t4 = query.isError ? <StatePanel alert={true} title={states.errorTitle} body={states.errorBody} action={<RetryButton label={states.retry} onRetry={retry} />} /> : null;
    $[16] = query.isError;
    $[17] = retry;
    $[18] = states;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return <div className="vt-stack">{t1}{days.map(day => <section key={day.key} className="vt-day" aria-label={day.label}><h3 className="vt-day-h"><SecGlyph className="vt-glyph" />{day.label}<span>{nf.format(day.items.length)}</span></h3><ol className="vt-events">{day.items.map(a_0 => {
          const kind = eventKind(a_0);
          const word = EVENT_WORDS[kind][lang];
          const time = <time dateTime={a_0.timestamp}>{new Date(a_0.timestamp).toLocaleTimeString(locale, {
              hour: "2-digit",
              minute: "2-digit"
            })}</time>;
          if (isTransferEvent(a_0)) {
            const tokenId = eventTokenId(a_0);
            return <li key={activityKey(a_0)} className="vt-ev" data-tone={eventTone(kind)}><span className="vt-ev-mark" aria-hidden={true}>{MARK[kind]}</span><div className="vt-ev-body"><p className="vt-ev-head"><span className="vt-ev-kind">{word}</span><span className="vt-ev-token">{tokenId ? `#${tokenDigits(tokenId, isFa)}` : "\u2014"}</span>{time}</p><p className="vt-ev-path">{kind === "mint" ? <span className="vt-origin">{t.mintOrigin}</span> : <Who name={a_0.from_address?.wallet?.user?.name} address={a_0.from_address?.address} />}<Toward aria-label={t.columnTo} className="vt-ev-arrow" /><Who name={a_0.to_address?.wallet?.user?.name} address={a_0.to_address?.address} /></p></div><TxLink chain={a_0.chain} hash={a_0.tx_hash} label={t.viewTransaction} /></li>;
          }
          const price = eventPrice(a_0);
          const shown = price ? tokenAmount(price.amount, locale) : null;
          const market = marketName(a_0.marketplace);
          return <li key={activityKey(a_0)} className="vt-ev" data-tone={eventTone(kind)}><span className="vt-ev-mark" aria-hidden={true}>{MARK[kind]}</span><div className="vt-ev-body"><p className="vt-ev-head"><span className="vt-ev-kind">{word}</span><EventWork e={a_0} contract={contract} lang={lang} />{time}</p><p className="vt-ev-path"><Account e={a_0} lang={lang} />{shown && price ? <span className="vt-ev-price"><b>{shown}</b><small>{price.symbol}</small></span> : null}{market ? <span className="vt-market">{market}</span> : null}</p></div><TxLink chain={a_0.chain} hash={a_0.type === "offer" ? a_0.tx_hash : null} label={t.viewTransaction} /></li>;
        })}</ol></section>)}<div className="v2-cd-foot"><p aria-live="polite" className="v2-cd-count">{t.activityCount.replace("{count}", nf.format(rows.length))}</p>{t2}{t3}{stop === "cap" ? <Note>{t.activityCapReached.replace("{count}", nf.format(ACTIVITY_MAX_PAGES * ACTIVITY_PAGE_SIZE))}</Note> : null}{t4}</div></div>;
}